<script setup>
import {ref} from "vue";
import router from "@/router";
import {useI18n} from "vue-i18n";
import {useDataTable} from "@/utils/datatableHelper";
import {formatDate, formatNumber} from "@/helper";
import BondService from "@/services/BondService";
import {useBondsPageGuard} from '@/utils/features';

/**
 * IMA-517: menu Dluhopisy v administraci – seznam všech dluhopisů načtených z ESO (ADMIN / SUPERADMIN / BACK_OFFICE).
 */
const {t} = useI18n();
// v instanci bez dluhopisů (B.I.G. Invest SK/CZ) stránka přesměruje na nástěnku
useBondsPageGuard();

// výchozí řazení podle splatnosti (BE: BondController::buildOrderQuery)
// nastavení tabulky (stránka, počet řádků, řazení, filtry) se pamatuje v localStorage pod klíčem BONDS-INDEX
const datatableRef = ref(null);
const datatable = useDataTable(fetchBonds, 'maturity_date', 10, true, {}, 'BONDS-INDEX', datatableRef);
const lastSync = ref(null);

/** Barva štítku stavu prodeje (stav počítá BE z období prodeje a splatnosti). */
const saleStateBadge = {
  ON_SALE: 'bg-success',
  PREPARING: 'bg-primary',
  CLOSED: 'bg-secondary',
  UNKNOWN: 'bg-warning text-dark',
};

const columns = ref([
  {label: t('bonds.name'), field: 'name', filterOptions: {enabled: true, placeholder: ' '}},
  {label: t('bonds.isin'), field: 'isin', filterOptions: {enabled: true, placeholder: ' '}},
  {
    label: t('bonds.sale_state'),
    field: 'sale_state',
    sortable: false,
    filterOptions: {
      enabled: true,
      placeholder: t('bonds.all_states'),
      filterDropdownItems: ['ON_SALE', 'PREPARING', 'CLOSED', 'UNKNOWN'].map(state => ({value: state, text: t('bonds.sale_states.' + state)})),
    },
  },
  {label: t('bonds.coupon_rate'), field: 'coupon_rate', type: 'number'},
  {label: t('bonds.coupon_frequency'), field: 'coupon_frequency_label', sortable: false},
  {label: t('bonds.sale_to'), field: 'sale_to'},
  {label: t('bonds.maturity_date'), field: 'maturity_date'},
  {label: t('bonds.volume'), field: 'volume', sortable: false},
  {label: t('bonds.clients_count'), field: 'clients_count', type: 'number', sortable: false},
  {label: '', field: 'action', sortable: false},
]);

function fetchBonds(body) {
  datatable.isLoading.value = true;
  BondService.fetchRecords(body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    // čas posledního importu katalogu; dokud po nasazení neproběhl, jen datum exportu z ESO
    lastSync.value = response.meta.last_import_at
        ? formatDate(response.meta.last_import_at, 'd.m.Y H:i')
        : (response.meta.last_export_date ? formatDate(response.meta.last_export_date, 'd.m.Y') : null);
    datatable.isLoading.value = false;
  });
}

/** Upsaný / celkový objem v milionech; význam objemů z ESO se ještě ověřuje (Q23), proto bez přepočtu na volný objem. */
function formatVolume(row) {
  if (row.emission_volume === null && row.subscribed_volume === null) {
    return '---';
  }
  const millions = value => value === null ? '?' : formatNumber(value / 1000000, 1);
  return millions(row.subscribed_volume) + ' / ' + millions(row.emission_volume) + ' ' + t('bonds.millions_currency', {currency: row.currency ?? 'CZK'});
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-3">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('general.back') }}</a>
        </div>
        <div class="col-6 text-center">
          <div class="page-section__title">{{ t('bonds.title') }}</div>
          <div v-if="lastSync" class="page-section__subtitle">{{ t('bonds.last_sync', {date: lastSync}) }}</div>
        </div>
        <div class="col-3"></div>
      </div>
    </div>
  </div>
  <div class="page-section__content">
    <div class="mt-3">
      <vue-good-table
          ref="datatableRef"
          :sort-options="{enabled: true, initialSortBy: datatable.initialSortBy.value}"
          :columns="columns"
          :rows="datatable.rows.value"
          mode="remote"
          @page-change="datatable.onPageChange"
          @sort-change="datatable.onSortChange"
          @column-filter="datatable.onColumnFilter"
          @per-page-change="datatable.onPerPageChange"
          :totalRows="datatable.totalRecords.value"
          :pagination-options="{
                  enabled: true,
                  rowsPerPageLabel: t('strategies.entriesPerPage'),
                  nextLabel: t('strategies.next'),
                  prevLabel: t('strategies.previous'),
                  ofLabel: t('strategies.of'),
                }">
        <template #table-row="props">
          <span v-if="props.column.field === 'sale_state'" class="badge" :class="saleStateBadge[props.row.sale_state]">{{ t('bonds.sale_states.' + props.row.sale_state) }}</span>
          <span v-else-if="props.column.field === 'coupon_rate'">{{ props.row.coupon_rate === null ? '---' : formatNumber(props.row.coupon_rate) + ' %' }}</span>
          <span v-else-if="props.column.field === 'sale_to'">{{ props.row.sale_to ? formatDate(props.row.sale_to, 'd.m.Y') : '---' }}</span>
          <span v-else-if="props.column.field === 'maturity_date'">{{ props.row.maturity_date ? formatDate(props.row.maturity_date, 'd.m.Y') : '---' }}</span>
          <span v-else-if="props.column.field === 'volume'">{{ formatVolume(props.row) }}</span>
          <span v-else-if="props.column.field === 'action'">
            <span style="font-weight: bold; float: right">
              <router-link :to="{ name: 'SecurityDetail', params: { id: props.row.id} }"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
            </span>
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] ?? '---' }}</span>
        </template>
        <template v-slot:emptystate v-if="!datatable.isLoading.value">
          <div class="text-center">{{ t('bonds.no_records_found') }}</div>
        </template>
        <template v-slot:emptystate v-if="datatable.isLoading.value">
          <div class="text-center">{{ t('strategies.data_loading') }}</div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>
